import { debounce } from 'es-toolkit';
import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { Fade, Paper, Theme } from '@mui/material';

import { Point2D } from '@paragon/contract-library';

import { setTooltipHidden } from '../../../store/actions/viewportActions';
import { useAppDispatch } from '../../hooks';

const TOOLTIP_DELAY = 200;

const tooltipContext = createContext({ flushPositionChanges: () => {} });
export function useTooltipContext() {
  return useContext(tooltipContext);
}

export function TooltipBase(props: PropsWithChildren<{ insistent?: boolean }>) {
  const dispatch = useAppDispatch();
  const [position, __rawSetPosition] = useState<Point2D | null>(null);

  const debouncedSetPosition = useMemo(
    () => debounce(__rawSetPosition, TOOLTIP_DELAY, { edges: ['trailing'] }),
    [__rawSetPosition],
  );

  const setPosition = props.insistent ? __rawSetPosition : debouncedSetPosition;

  const flushPositionChanges = useMemo(
    () => (props.insistent ? () => {} : debouncedSetPosition.flush.bind(debouncedSetPosition)),
    [props.insistent, debouncedSetPosition],
  );

  const cancelPositionChanges = useMemo(
    () => (props.insistent ? () => {} : debouncedSetPosition.cancel.bind(debouncedSetPosition)),
    [props.insistent, debouncedSetPosition],
  );

  const contextValue = useMemo(() => ({ flushPositionChanges }), [flushPositionChanges]);

  useEffect(() => {
    function onMouseDown() {
      dispatch(setTooltipHidden(true));
    }

    function onMouseMove(event: MouseEvent) {
      setPosition({ x: event.clientX, y: event.clientY });
    }

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mousedown', onMouseDown);

    return () => {
      cancelPositionChanges();

      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mousedown', onMouseDown);
    };
  }, [cancelPositionChanges, dispatch, setPosition]);

  if (position == null) return null;

  return (
    <Fade in>
      <Paper
        elevation={6}
        sx={{
          display: 'block',
          position: 'fixed',

          '&:empty': {
            display: 'none',
          },

          padding: 1,

          top: position.y + 12,
          left: position.x,

          minWidth: 80,

          height: 'fit-content',
          textAlign: 'center',
          pointerEvents: 'none',
          userSelect: 'none',

          zIndex: (theme: Theme) => theme.zIndex.drawer + 1,
        }}
      >
        <tooltipContext.Provider value={contextValue}>{props.children}</tooltipContext.Provider>
      </Paper>
    </Fade>
  );
}
